import { useMemo, useState } from "react";

import type {
  ConnectedAccount,
  PlatformId,
  PlatformInfo,
  ScheduledPost,
  TikTokOptions,
  TikTokPrivacyLevel,
} from "../api";
import { PlatformIcon } from "./PlatformIcon";

// Add or remove one platform across EVERY scheduled post in the workspace, so a
// creator who connects (say) TikTok after building a queue doesn't have to edit
// each post by hand. Adding TikTok needs a privacy level (its audit rule), so
// the row exposes a small picker; the other interaction toggles default sensibly
// and can be fine-tuned per post via Edit.

const TT_PRIVACY: { value: TikTokPrivacyLevel; label: string }[] = [
  { value: "PUBLIC_TO_EVERYONE", label: "Public" },
  { value: "MUTUAL_FOLLOW_FRIENDS", label: "Friends" },
  { value: "FOLLOWER_OF_CREATOR", label: "Followers" },
  { value: "SELF_ONLY", label: "Only me" },
];

function defaultTikTok(privacy: TikTokPrivacyLevel): TikTokOptions {
  return {
    privacyLevel: privacy,
    disableComment: false,
    disableDuet: false,
    disableStitch: false,
    commercialContent: false,
    yourBrand: false,
    brandedContent: false,
  };
}

export function BulkPlatformBar({
  scheduled,
  platforms,
  accounts,
  onApply,
}: {
  scheduled: ScheduledPost[];
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  /** Apply the change server-side and refresh; resolves with the counts. */
  onApply: (
    platform: PlatformId,
    action: "add" | "remove",
    tiktok?: TikTokOptions,
  ) => Promise<{ updated: number; total: number }>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [ttPrivacy, setTtPrivacy] = useState<TikTokPrivacyLevel>("PUBLIC_TO_EVERYONE");

  const connected = useMemo(() => new Set(accounts.map((a) => a.platform)), [accounts]);
  const total = scheduled.length;

  const coverage = useMemo(() => {
    const m = new Map<PlatformId, number>();
    for (const s of scheduled) {
      for (const id of s.platforms as PlatformId[]) m.set(id, (m.get(id) ?? 0) + 1);
    }
    return m;
  }, [scheduled]);

  const rows = platforms.filter((p) => connected.has(p.id));

  async function apply(platform: PlatformInfo, action: "add" | "remove") {
    setBusy(`${platform.id}:${action}`);
    setNote(null);
    try {
      const tiktok =
        action === "add" && platform.id === "tiktok" ? defaultTikTok(ttPrivacy) : undefined;
      const r = await onApply(platform.id, action, tiktok);
      setNote(
        r.updated === 0
          ? `No posts needed changing for ${platform.name}.`
          : `${action === "add" ? "Added" : "Removed"} ${platform.name} ${
              action === "add" ? "to" : "from"
            } ${r.updated} post${r.updated === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(null);
    }
  }

  if (total === 0 || rows.length === 0) return null;

  return (
    <div className="bulk-plat">
      <button
        type="button"
        className="bulk-plat-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">⚡</span> Bulk edit platforms
        <span className="bulk-plat-caret" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
      </button>

      {open && (
        <div className="bulk-plat-panel">
          <p className="muted small bulk-plat-lead">
            Add or remove a platform across all {total} scheduled post{total === 1 ? "" : "s"} in
            this workspace.
          </p>
          {rows.map((p) => {
            const on = coverage.get(p.id) ?? 0;
            const canAdd = on < total;
            const canRemove = on > 0;
            return (
              <div key={p.id} className="bulk-plat-row">
                <span className="bulk-plat-name">
                  <PlatformIcon platform={p} size={16} />
                  {p.name}
                </span>
                <span className="muted small bulk-plat-count">
                  on {on}/{total}
                </span>
                <span className="bulk-plat-actions">
                  {p.id === "tiktok" && canAdd && (
                    <select
                      className="bulk-plat-priv"
                      value={ttPrivacy}
                      onChange={(e) => setTtPrivacy(e.target.value as TikTokPrivacyLevel)}
                      aria-label="TikTok privacy for added posts"
                      disabled={busy !== null}
                    >
                      {TT_PRIVACY.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    type="button"
                    className="btn small"
                    disabled={!canAdd || busy !== null}
                    onClick={() => apply(p, "add")}
                  >
                    {busy === `${p.id}:add` ? "Adding…" : "Add to all"}
                  </button>
                  <button
                    type="button"
                    className="btn small ghost"
                    disabled={!canRemove || busy !== null}
                    onClick={() => apply(p, "remove")}
                  >
                    {busy === `${p.id}:remove` ? "Removing…" : "Remove"}
                  </button>
                </span>
              </div>
            );
          })}
          {note && (
            <p className="bulk-plat-note" role="status">
              {note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
